import { useEffect, useRef, useState } from 'react';
import { NavigateFunction, Location } from 'react-router-dom';
import logo from '../../assets/imgs/hotel_peristil_white.svg';
import scrollToPage from '../../utils/scrollToPage';
import useScrollDirection from '../../utils/useScrollDirection';
import {
  ACCOMMODATIONS,
  BOOK_NOW_LINK,
  FOOTER,
  GALLERY,
  HOME,
  PROPERTY_NAME,
  RESTAURANT,
} from '../../utils/constants';
import Variables from '../../style/variables.scss';

type NavbarProps = {
  location: Location;
  navigate: NavigateFunction;
  backgroundAlwaysVisible?: boolean;
};

const Navbar = ({ location, navigate, backgroundAlwaysVisible = false }: NavbarProps) => {
  const direction = useScrollDirection();
  const [openMenu, setOpenMenu] = useState<boolean>(false);

  const headerRef = useRef<HTMLElement | null>(null);
  const headerBgRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    headerRef.current = document.getElementById('nav_container') as HTMLElement | null;
    headerBgRef.current = document.getElementById('nav_background') as HTMLElement | null;

    if (headerBgRef.current) {
      headerBgRef.current.style.background = backgroundAlwaysVisible
        ? Variables.auxNeutral
        : 'transparent';
    }
  }, [backgroundAlwaysVisible]);

  const handleLink = (target: string) => {
    setOpenMenu((currentState) => !currentState);
    scrollToPage(target, location, navigate);

    setTimeout(() => {
      const header = headerRef.current;
      const headerBackground = headerBgRef.current;
      if (header && headerBackground && window.scrollY > 96) {
        headerBackground.style.top = '-6rem';
        header.style.top = '-6rem';
      }
    }, 1100);
  };

  const handleHeaderAppearance = () => {
    const header = headerRef.current;
    const headerBackground = headerBgRef.current;
    if (!header || !headerBackground) return;

    if (direction === 'up') {
      headerBackground.style.top = '0';
      header.style.top = '0';

      if (!backgroundAlwaysVisible) {
        headerBackground.style.background = window.scrollY < 96 ? 'transparent' : Variables.auxNeutral;
      } else {
        headerBackground.style.background = Variables.auxNeutral;
      }
    } else if (direction === 'down') {
      headerBackground.style.top = '-6rem';
      header.style.top = '-6rem';

      headerBackground.style.background = backgroundAlwaysVisible
        ? Variables.auxNeutral
        : (window.scrollY >= 96 ? Variables.auxNeutral : 'transparent');
    }
  };

  useEffect(() => {
    const onScroll = () => handleHeaderAppearance();
    window.addEventListener('scroll', onScroll, { passive: true });

    handleHeaderAppearance();
    return () => window.removeEventListener('scroll', onScroll);
  }, [direction, backgroundAlwaysVisible]);

  useEffect(() => {
    if (openMenu) {
      document.body.classList.add('lock_scroll');
      document.getElementById('nav_menu')?.classList.add('open_sidemenu');
    } else {
      document.body.classList.remove('lock_scroll');
      document.getElementById('nav_menu')?.classList.remove('open_sidemenu');
    }
  }, [openMenu]);

  return (
    <div className='nav_wrapper'>
      <div id='nav_container'>
        <div className='nav_menu'>
          <div className='nav_menu_burger'>
            <input
              className='nav_menu_burger_cheeckbox'
              type='checkbox'
              onChange={() => setOpenMenu(!openMenu)}
              checked={openMenu}
              aria-label='Burger'
            />
            <div>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
        <div className='nav_logo'>
          <img
            src={logo}
            alt={PROPERTY_NAME + ' logo'}
            id='nav_logo'
            className='pointer'
            onClick={() => {
              scrollToPage('home', location, navigate);
              setOpenMenu(false);
            }}
          />
        </div>
      </div>
      <div id='nav_background'></div>
      <div id='nav_menu'>
        <div className='nav_menu_list'>
          <div className='nav_menu_list_links'>
            <div className='opacity_transition_high' onClick={() => handleLink(HOME)}>
              Home
            </div>
            <div className='opacity_transition_high'>
              <a href={BOOK_NOW_LINK} target='_blank' rel='noreferrer'>
                Book now
              </a>
            </div>
            <div className='opacity_transition_high' onClick={() => handleLink(ACCOMMODATIONS)}>
              Accommodation
            </div>
            <div className='opacity_transition_high' onClick={() => handleLink(RESTAURANT)}>
              Restaurant
            </div>
            <div className='opacity_transition_high' onClick={() => handleLink(GALLERY)}>
              Gallery
            </div>
            <div className='opacity_transition_high' onClick={() => handleLink(FOOTER)}>
              Contact us
            </div>
          </div>
          <div className='nav_menu_list_image'></div>
        </div>
      </div>
    </div>
  );
};

export default Navbar;